/**
 * API клиент для работы с уведомлениями колокольчика
 * Поддерживает браузер и Telegram режимы
 */

import { http } from './http'

// ============================================
// ТИПЫ ДАННЫХ
// ============================================

export interface UserNotification {
  id: string
  type: 'status_change' | 'message' | 'system' | 'reminder' | 'calc_cancel'
  title: string
  message: string
  order_id?: string
  offer_id?: string
  related_data?: Record<string, any>
  is_read: boolean
  read_at?: string
  priority: 'low' | 'normal' | 'high' | 'urgent'
  category?: string
  created_at: string
}

export interface NotificationListResponse {
  success: boolean
  notifications: UserNotification[]
  total_count: number
  unread_count: number
  has_more: boolean
}

export interface NotificationStatsResponse {
  success: boolean
  unread_count: number
  total_count: number
  by_type: Record<string, number>
  by_priority: Record<string, number>
}

export interface NotificationSettings {
  status_change_enabled: boolean
  message_enabled: boolean
  system_enabled: boolean
  reminder_enabled: boolean
  min_priority: 'low' | 'normal' | 'high' | 'urgent'
  auto_delete_read_after_days: number
  auto_delete_unread_after_days: number
}

export interface NotificationSettingsResponse {
  success: boolean
  settings: NotificationSettings
}

// ============================================
// API МЕТОДЫ
// ============================================

/**
 * Получить список уведомлений пользователя
 */
export const getNotifications = async (params: {
  limit?: number
  offset?: number
  only_unread?: boolean
  type_filter?: string
} = {}): Promise<NotificationListResponse> => {
  const searchParams = new URLSearchParams()
  
  if (params.limit !== undefined) searchParams.set('limit', params.limit.toString())
  if (params.offset !== undefined) searchParams.set('offset', params.offset.toString())
  if (params.only_unread !== undefined) searchParams.set('only_unread', params.only_unread.toString())
  if (params.type_filter) searchParams.set('type_filter', params.type_filter)
  
  const response = await http.get(`/api/notifications/list?${searchParams.toString()}`)
  return response.data
}

/**
 * Получить статистику уведомлений (для колокольчика)
 */
export const getNotificationStats = async (): Promise<NotificationStatsResponse> => {
  const response = await http.get('/api/notifications/stats')
  return response.data
}

/**
 * Отметить конкретные уведомления как прочитанные
 */
export const markNotificationsRead = async (notificationIds: string[]): Promise<{
  success: boolean
  marked_count: number
  message: string
}> => {
  const response = await http.post('/api/notifications/mark-read', {
    notification_ids: notificationIds
  })
  return response.data
}

/**
 * Отметить ВСЕ уведомления как прочитанные
 */
export const markAllNotificationsRead = async (): Promise<{
  success: boolean
  marked_count: number
  message: string
}> => {
  const response = await http.post('/api/notifications/mark-all-read')
  return response.data
}

/**
 * Удалить уведомление
 */
export const deleteNotification = async (notificationId: string): Promise<{
  success: boolean
  message: string
}> => {
  const response = await http.delete(`/api/notifications/delete/${notificationId}`)
  return response.data
}

/**
 * Удалить все уведомления пользователя
 */
export const deleteAllNotifications = async (): Promise<{
  success: boolean
  message: string
  deleted_count: number
}> => {
  const response = await http.delete(`/api/notifications/delete-all`)
  return response.data
}

/**
 * Удалить все уведомления по конкретному заказу
 */
export const deleteNotificationsByOrder = async (orderId: string): Promise<{
  success: boolean
  message: string
  deleted_count: number
}> => {
  const response = await http.delete(`/api/notifications/delete-by-order/${orderId}`)
  return response.data
}

/**
 * Получить настройки уведомлений пользователя
 */
export const getNotificationSettings = async (): Promise<NotificationSettingsResponse> => {
  const response = await http.get('/api/notifications/settings')
  return response.data
}

/**
 * Обновить настройки уведомлений пользователя
 */
export const updateNotificationSettings = async (updates: Partial<NotificationSettings>): Promise<{
  success: boolean
  message: string
  settings: NotificationSettings
}> => {
  const response = await http.put('/api/notifications/settings', updates)
  return response.data
}

// ============================================
// УТИЛИТЫ
// ============================================

/**
 * Получить иконку для типа уведомления
 */
export const getNotificationIcon = (type: UserNotification['type']): string => {
  const icons = {
    status_change: '🔄',
    message: '💬',
    system: '⚙️',
    reminder: '⏰',
    calc_cancel: '💡'
  }
  return icons[type] || '📢'
}

/**
 * Получить цвет для приоритета уведомления
 */
export const getPriorityColor = (priority: UserNotification['priority']): string => {
  const colors = {
    low: 'text-gray-600',
    normal: 'text-blue-600',
    high: 'text-orange-600',
    urgent: 'text-red-600'
  }
  return colors[priority] || 'text-gray-600'
}

/**
 * Получить цвет фона для приоритета уведомления
 */
export const getPriorityBgColor = (priority: UserNotification['priority']): string => {
  const colors = {
    low: 'bg-gray-50',
    normal: 'bg-blue-50',
    high: 'bg-orange-50',
    urgent: 'bg-red-50'
  }
  return colors[priority] || 'bg-gray-50'
}

/**
 * Форматировать время уведомления
 */
export const formatNotificationTime = (timestamp: string): string => {
  const date = new Date(timestamp)
  const now = new Date()
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))
  
  if (diffInMinutes < 1) {
    return 'только что'
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes} мин назад`
  } else if (diffInMinutes < 1440) { // 24 часа
    const hours = Math.floor(diffInMinutes / 60)
    return `${hours} ч назад`
  } else {
    const days = Math.floor(diffInMinutes / 1440)
    if (days === 1) {
      return 'вчера'
    } else if (days < 7) {
      return `${days} дн назад`
    } else {
      return date.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'short'
      })
    }
  }
}

/**
 * Получить текст статуса заказа на русском
 */
export const getOrderStatusText = (status: string): string => {
  const statusTexts: Record<string, string> = {
    'new': 'Новый',
    'watching': 'Просмотр',
    'calculating': 'На просчете',
    'price_given': 'Оценен',
    'pending_confirmation': 'Ждет подтверждения',
    'executing': 'Выполняется',
    'defect': 'Брак',
    'rejected': 'Отказ',
    'completed': 'Выполнен',
    'archived': 'Архив'
  }
  return statusTexts[status] || status
}
